/**
 * ExploreView - Phase D3.3
 * Explore screen with real Lisbon map using Leaflet
 * Supports voice and keyboard navigation
 *
 * Features:
 * - Auto-zoom to fit all pins on load
 * - Consistent voice via ZaxiaVoiceService
 * - Full-height experience detail panel with concierge-style presentation
 * - Voice commands for exploring details
 * - Phase D3.3: Voice-first booking flow with transport selection
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Mic, Clock, MapPin, Euro, Car, Star, ChevronLeft, Check, X, Bus, CalendarPlus } from 'lucide-react';
import { EXPLORE_PINS, LISBON_MAP, parseExploreVoice } from '@/data/exploreData';
import type { ExplorePin } from '@/data/exploreData';
import type { CreateBookingRequest } from '@/api';
import { zaxiaVoice, createSpeechRecognition, isSpeechRecognitionAvailable, type SpeechRecognitionEvent } from '@/services';
import { processVoiceInput, createVoiceContext, type VoiceAction } from '@/voice';

// =============================================================================
// TYPES
// =============================================================================

// Phase D3.3: Booking flow states
type BookingFlowState =
  | 'idle'           // No booking in progress
  | 'askingTransport' // Asked if they want transport
  | 'confirming'     // Waiting for yes/no confirmation
  | 'booked';        // Booking complete, show confirmation

type TransportChoice = 'taxi' | 'walk' | 'public' | null;

// Phase D3.3: Voice hints type (imported from ConciergeGrid conceptually)
export interface VoiceHintConfig {
  title: string;
  hints: string[];
  variant?: 'default' | 'action' | 'confirm';
}

interface ExploreViewProps {
  isActive: boolean;
  onClose: () => void;
  selectedPinType?: ExplorePin['type'] | null;
  onPinTypeHandled?: () => void;
  // Phase D3.3: Booking handler
  onBookingCreated?: (booking: CreateBookingRequest) => Promise<void>;
  // Phase D3.3: Global voice hints callback
  onVoiceHintsChange?: (hints: VoiceHintConfig | null) => void;
}

// Custom marker icons
const createIcon = (emoji: string, color: string, isSelected: boolean) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: ${isSelected ? '50px' : '44px'};
        height: ${isSelected ? '50px' : '44px'};
        background: linear-gradient(135deg, ${color} 0%, ${color}dd 100%);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${isSelected ? '24px' : '20px'};
        border: 3px solid white;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        transition: all 0.2s ease;
        ${isSelected ? 'transform: scale(1.15);' : ''}
      ">
        ${emoji}
      </div>
    `,
    iconSize: [isSelected ? 50 : 44, isSelected ? 50 : 44],
    iconAnchor: [isSelected ? 25 : 22, isSelected ? 25 : 22],
  });
};

const hotelIcon = L.divIcon({
  className: 'hotel-marker',
  html: `
    <div style="
      width: 48px;
      height: 48px;
      background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 22px;
      border: 3px solid white;
      box-shadow: 0 4px 15px rgba(245, 158, 11, 0.4);
    ">
      🏨
    </div>
  `,
  iconSize: [48, 48],
  iconAnchor: [24, 24],
});

// Calculate bounds to fit all pins + hotel
function calculateBounds(): L.LatLngBounds {
  const allPoints: [number, number][] = [
    [LISBON_MAP.coordinates.lat, LISBON_MAP.coordinates.lng],
    ...EXPLORE_PINS.map((pin: ExplorePin) => [pin.coordinates.lat, pin.coordinates.lng] as [number, number])
  ];
  return L.latLngBounds(allPoints);
}

// Component to handle map bounds and focus
function MapController({
  selectedPin,
  shouldFitBounds,
  isPanelOpen,
  triggerZoomOut
}: {
  selectedPin: ExplorePin | null;
  shouldFitBounds: boolean;
  isPanelOpen: boolean;
  triggerZoomOut: number; // Increment to trigger zoom out
}) {
  const map = useMap();
  const hasFittedBounds = useRef(false);
  const prevIsPanelOpen = useRef(isPanelOpen);

  // Fit bounds on initial load - zoom to show all pins
  useEffect(() => {
    if (shouldFitBounds && !hasFittedBounds.current) {
      // Small delay to ensure container is ready
      setTimeout(() => {
        map.invalidateSize();
        const bounds = calculateBounds();
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
        hasFittedBounds.current = true;
      }, 100);
    }
  }, [shouldFitBounds, map]);

  // Reset so re-entering always re-fits to show all pins
  useEffect(() => {
    if (!shouldFitBounds) {
      hasFittedBounds.current = false;
    }
  }, [shouldFitBounds]);

  // When panel closes (true → false): wait for spring animation to finish,
  // then invalidate size and re-fit all pins. This handles ALL close paths:
  // button click, Escape key, ArrowLeft/Right, voice command, booking complete.
  useEffect(() => {
    const wasOpen = prevIsPanelOpen.current;
    prevIsPanelOpen.current = isPanelOpen;

    if (wasOpen && !isPanelOpen) {
      // Spring animation (35% → 100%) takes ~400ms — wait for it to settle
      const timeout = setTimeout(() => {
        map.invalidateSize();
        const bounds = calculateBounds();
        map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 13, duration: 0.5 });
      }, 400);
      return () => clearTimeout(timeout);
    }
  }, [isPanelOpen, map]);

  // Fly to selected pin when one is chosen
  useEffect(() => {
    if (selectedPin) {
      map.flyTo([selectedPin.coordinates.lat, selectedPin.coordinates.lng], 14, {
        duration: 0.5,
      });
    }
  }, [selectedPin, map]);

  // Explicit zoom-out trigger (voice "show all" when panel is already closed)
  useEffect(() => {
    if (triggerZoomOut > 0) {
      map.invalidateSize();
      const bounds = calculateBounds();
      map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 13, duration: 0.5 });
    }
  }, [triggerZoomOut, map]);

  return null;
}

// =============================================================================
// EXPERIENCE DETAIL PANEL - Phase D3.2, D3.3
// Full-height concierge-style presentation with booking flow overlay
// =============================================================================
function ExperienceDetailPanel({
  pin,
  isOpen,
  onClose,
  bookingState,
  transportChoice,
  showConfirmation,
  onBookThis,
  onSelectTransport,
  onConfirmBooking,
  onCancelBooking,
  panelFocusIndex,
}: {
  pin: ExplorePin | null;
  isOpen: boolean;
  onClose: () => void;
  bookingState: BookingFlowState;
  transportChoice: TransportChoice;
  showConfirmation: boolean;
  onBookThis: () => void;
  onSelectTransport: (t: 'taxi' | 'walk' | 'public') => void;
  onConfirmBooking: () => void;
  onCancelBooking: () => void;
  panelFocusIndex: number;
}) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Reset image index when pin changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [pin?.id]);

  // Auto-cycle through images
  useEffect(() => {
    if (!isOpen || !pin) return;

    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % (pin.images?.length || 1));
    }, 4000);

    return () => clearInterval(interval);
  }, [isOpen, pin]);

  if (!pin) return null;

  const images = pin.images || [];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="absolute inset-0 z-[1001] flex"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Dimmed map backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

          {/* Detail panel - slides from right */}
          <motion.div
            className="absolute right-0 top-0 w-[65%] h-full bg-zinc-900 flex flex-col shadow-2xl"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.22, ease: 'easeOut' }}
          >
            {/* Image carousel - top 38% (reduced from 45% to make room for fixed footer) */}
            <div className="explore-panel-image relative h-[38%] overflow-hidden flex-shrink-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentImageIndex}
                  className="absolute inset-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                >
                  <img
                    src={images[currentImageIndex] || `https://via.placeholder.com/800x600?text=${encodeURIComponent(pin.name)}`}
                    alt={`${pin.name} - Image ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/20 to-transparent" />
                </motion.div>
              </AnimatePresence>

              {/* Image indicators */}
              {images.length > 1 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === currentImageIndex
                          ? 'bg-white scale-125'
                          : 'bg-white/40 hover:bg-white/60'
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* Hotel badge */}
              <div className="absolute top-2 left-2">
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium flex items-center gap-1 ${
                  pin.hotelBadge === 'partner'
                    ? 'bg-amber-500/90 text-white'
                    : 'bg-white/90 text-zinc-800'
                }`}>
                  <Star className="w-2 h-2" />
                  {pin.hotelBadge === 'partner' ? 'Hotel Partner' : 'Hotel Recommended'}
                </span>
              </div>
            </div>

            {/* Content area - scrollable */}
            <div className="explore-detail-body flex-1 overflow-hidden p-3 flex flex-col">
              {/* Header */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{pin.icon}</span>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-semibold text-white leading-tight">{pin.name}</h2>
                  <p className="text-white/60 text-[10px] truncate">{pin.subtitle}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                  pin.type === 'museum'
                    ? 'bg-purple-500/20 text-purple-300'
                    : pin.type === 'beach'
                    ? 'bg-cyan-500/20 text-cyan-300'
                    : 'bg-orange-500/20 text-orange-300'
                }`}>
                  {pin.type.charAt(0).toUpperCase() + pin.type.slice(1)}
                </span>
              </div>

              {/* Info cards — single row of 4, no circles */}
              <div className="grid grid-cols-4 gap-1.5 mb-2">
                <div className="bg-white/5 rounded-md py-1.5 px-1 flex flex-col items-center gap-0.5 text-center">
                  <MapPin className="w-3 h-3 text-amber-400" />
                  <p className="text-white/50 text-[8px] uppercase tracking-wide leading-none">Distance</p>
                  <p className="text-white text-[10px] font-medium leading-tight">{pin.distance}</p>
                </div>

                <div className="bg-white/5 rounded-md py-1.5 px-1 flex flex-col items-center gap-0.5 text-center">
                  <Car className="w-3 h-3 text-blue-400" />
                  <p className="text-white/50 text-[8px] uppercase tracking-wide leading-none">Travel</p>
                  <p className="text-white text-[10px] font-medium leading-tight">{pin.travelTime}</p>
                </div>

                <div className="bg-white/5 rounded-md py-1.5 px-1 flex flex-col items-center gap-0.5 text-center">
                  <Clock className="w-3 h-3 text-emerald-400" />
                  <p className="text-white/50 text-[8px] uppercase tracking-wide leading-none">Hours</p>
                  <p className="text-white text-[10px] font-medium leading-tight">{pin.openingHours}</p>
                </div>

                {pin.priceRange && (
                  <div className="bg-white/5 rounded-md py-1.5 px-1 flex flex-col items-center gap-0.5 text-center">
                    <Euro className="w-3 h-3 text-rose-400" />
                    <p className="text-white/50 text-[8px] uppercase tracking-wide leading-none">Price</p>
                    <p className="text-white text-[10px] font-medium leading-tight">{pin.priceRange}</p>
                  </div>
                )}
              </div>

              {/* Concierge description */}
              <div className="flex-1 min-h-0">
                <h3 className="text-white/50 text-[9px] uppercase tracking-wider mb-1">About this place</h3>
                <p className="text-white/80 text-[10px] leading-snug whitespace-pre-line">
                  {pin.conciergeDescription}
                </p>
              </div>
            </div>

            {/* Action bar — TV remote navigable, mirrors voice booking flow */}
            <div className="flex-shrink-0 px-4 py-3 border-t border-white/[0.04] bg-zinc-950/40">
              {bookingState === 'idle' && (
                <div className="flex gap-2">
                  <motion.button
                    onClick={onBookThis}
                    className={`keyboard-focusable flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-green-500 text-white text-xs font-semibold rounded-xl px-4 py-2.5 transition-all${panelFocusIndex === 0 ? ' ring-2 ring-white shadow-lg' : ''}`}
                  >
                    <CalendarPlus className="w-3.5 h-3.5 flex-shrink-0" />
                    Book This Visit
                  </motion.button>
                  <motion.button
                    onClick={onClose}
                    className={`keyboard-focusable flex items-center justify-center gap-1.5 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] text-white/50 text-xs font-medium rounded-xl px-4 py-2.5 transition-all${panelFocusIndex === 1 ? ' ring-2 ring-white/50 shadow-lg' : ''}`}
                  >
                    <X className="w-3.5 h-3.5" />
                    Close
                  </motion.button>
                </div>
              )}
              {bookingState === 'askingTransport' && (
                <div className="flex gap-2">
                  {([
                    { key: 'taxi' as const, icon: Car, label: 'Taxi', sub: '~€15', color: 'text-amber-400', bg: 'hover:bg-amber-500/10 hover:border-amber-500/30' },
                    { key: 'walk' as const, icon: MapPin, label: 'Walk', sub: 'Self-guided', color: 'text-white/70', bg: 'hover:bg-white/[0.06] hover:border-white/10' },
                    { key: 'public' as const, icon: Bus, label: 'Tram', sub: 'Scenic', color: 'text-white/70', bg: 'hover:bg-white/[0.06] hover:border-white/10' },
                  ] as const).map(({ key, icon: Icon, label, sub, color, bg }, tidx) => (
                    <motion.button
                      key={key}
                      onClick={() => onSelectTransport(key)}
                      className={`keyboard-focusable flex-1 flex items-center justify-center gap-2 bg-white/[0.03] border border-white/[0.06] ${bg} ${color} rounded-xl px-3 py-2.5 transition-all${panelFocusIndex === tidx ? ' ring-2 ring-white/60 shadow-lg' : ''}`}
                    >
                      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                      <div className="text-left">
                        <div className="text-xs font-semibold leading-tight">{label}</div>
                        <div className="text-[9px] opacity-60 leading-tight">{sub}</div>
                      </div>
                    </motion.button>
                  ))}
                  <motion.button
                    onClick={onCancelBooking}
                    className={`keyboard-focusable flex items-center justify-center gap-1 bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.04] text-white/30 rounded-xl px-3 py-2.5 transition-all${panelFocusIndex === 3 ? ' ring-2 ring-white/30 shadow-lg' : ''}`}
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    <span className="text-xs">Back</span>
                  </motion.button>
                </div>
              )}
              {bookingState === 'confirming' && (
                <div className="flex gap-2">
                  <motion.button
                    onClick={onConfirmBooking}
                    className={`keyboard-focusable flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-green-500 text-white text-xs font-semibold rounded-xl px-4 py-2.5 transition-all${panelFocusIndex === 0 ? ' ring-2 ring-white shadow-lg' : ''}`}
                  >
                    <Check className="w-3.5 h-3.5 flex-shrink-0" />
                    Confirm{transportChoice === 'taxi' ? ' + Taxi' : ' Booking'}
                  </motion.button>
                  <motion.button
                    onClick={onCancelBooking}
                    className={`keyboard-focusable flex items-center justify-center gap-1.5 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] text-white/50 text-xs font-medium rounded-xl px-4 py-2.5 transition-all${panelFocusIndex === 1 ? ' ring-2 ring-white/50 shadow-lg' : ''}`}
                  >
                    <X className="w-3.5 h-3.5" />
                    Cancel
                  </motion.button>
                </div>
              )}
            </div>

            {/* Booking confirmation overlay */}
            <AnimatePresence>
              {showConfirmation && (
                <motion.div
                  className="absolute inset-0 flex items-center justify-center bg-zinc-900/95 z-10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <motion.div
                    className="text-center"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.1, type: 'spring', damping: 20 }}
                  >
                    <motion.div
                      className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', damping: 15 }}
                    >
                      <Check className="w-10 h-10 text-emerald-400" />
                    </motion.div>
                    <h3 className="text-2xl font-semibold text-white mb-2">Booking Confirmed</h3>
                    <p className="text-white/60 mb-2">{pin.name}</p>
                    {transportChoice === 'taxi' && (
                      <p className="text-amber-400 text-sm">Taxi arranged • €15</p>
                    )}
                    <p className="text-white/40 text-xs mt-4">Added to your calendar</p>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function ExploreView({
  isActive,
  onClose,
  selectedPinType,
  onPinTypeHandled,
  onBookingCreated,
  onVoiceHintsChange
}: ExploreViewProps) {
  const [focusedPinIndex, setFocusedPinIndex] = useState(0);
  const [selectedPin, setSelectedPin] = useState<ExplorePin | null>(null);
  const [showPanel, setShowPanel] = useState(false);
  const [zoomOutTrigger, setZoomOutTrigger] = useState(0);
  const hasSpokenSelectionRef = useRef(false);
  const hasMountedRef = useRef(false);

  // Phase D3.3: Booking flow state
  const [bookingState, setBookingState] = useState<BookingFlowState>('idle');
  const [transportChoice, setTransportChoice] = useState<TransportChoice>(null);
  const [showBookingConfirmation, setShowBookingConfirmation] = useState(false);

  // D-Pad focus index for the panel action bar (resets on panel/state change)
  const [panelFocusIndex, setPanelFocusIndex] = useState(0);

  // Voice recognition state (local voice - prevents Ask Zaxia from opening)
  const [voiceState, setVoiceState] = useState<'idle' | 'listening' | 'processing'>('idle');
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceFeedback, setVoiceFeedback] = useState<string | null>(null);
  const recognitionRef = useRef<ReturnType<typeof createSpeechRecognition>>(null);
  const transcriptRef = useRef('');

  // ==========================================================================
  // Phase D3.3: Update global voice hints based on state
  // ==========================================================================
  useEffect(() => {
    if (!onVoiceHintsChange) return;

    if (!showPanel) {
      // Map view - show explore hints
      onVoiceHintsChange({
        title: 'Say',
        hints: ['Show the museum', 'Beach', 'Restaurant'],
        variant: 'default',
      });
    } else if (bookingState === 'idle') {
      // Detail panel - idle
      onVoiceHintsChange({
        title: 'Ask me',
        hints: ['Book this', 'Tell me more', 'Is it far?'],
        variant: 'default',
      });
    } else if (bookingState === 'askingTransport') {
      // Asking about transport
      onVoiceHintsChange({
        title: 'Choose',
        hints: ['Taxi please', 'Walk there', 'Public transport'],
        variant: 'action',
      });
    } else if (bookingState === 'confirming') {
      // Confirming booking
      onVoiceHintsChange({
        title: 'Confirm',
        hints: ['Yes', 'Go back'],
        variant: 'confirm',
      });
    }
  }, [showPanel, bookingState, onVoiceHintsChange]);

  // Reset voice hints when leaving Explore
  useEffect(() => {
    return () => {
      onVoiceHintsChange?.(null);
    };
  }, [onVoiceHintsChange]);

  // Handle voice command to select pin type
  useEffect(() => {
    if (selectedPinType && isActive) {
      const pinIndex = EXPLORE_PINS.findIndex((p: ExplorePin) => p.type === selectedPinType);
      if (pinIndex !== -1) {
        setFocusedPinIndex(pinIndex);
        const pin = EXPLORE_PINS[pinIndex]!;
        setSelectedPin(pin);
        setShowPanel(true);
        zaxiaVoice.speak(`Showing ${pin.name}. ${pin.distance} from the hotel.`);
        hasSpokenSelectionRef.current = true;
      }
      onPinTypeHandled?.();
    }
  }, [selectedPinType, isActive, onPinTypeHandled]);

  useEffect(() => {
    if (isActive && !hasMountedRef.current) {
      hasMountedRef.current = true;
      setFocusedPinIndex(0);
      setSelectedPin(null);
      setShowPanel(false);
      hasSpokenSelectionRef.current = false;
    }
  }, [isActive]);

  useEffect(() => {
    if (!isActive) {
      hasMountedRef.current = false;
    }
  }, [isActive]);

  // ==========================================================================
  // VOICE RECOGNITION (local, prevents Ask Zaxia from opening)
  // ==========================================================================

  const selectPinByType = useCallback((type: ExplorePin['type']) => {
    const pinIndex = EXPLORE_PINS.findIndex((p: ExplorePin) => p.type === type);
    if (pinIndex !== -1) {
      const pin = EXPLORE_PINS[pinIndex]!;
      setFocusedPinIndex(pinIndex);
      setSelectedPin(pin);
      setShowPanel(true);
      // Reset booking state when selecting a new pin
      setBookingState('idle');
      setTransportChoice(null);
      // Use concierge-style introduction
      zaxiaVoice.speak(pin.voiceResponses.whatIsThis);
    }
  }, []);

  // ==========================================================================
  // Phase D3.3: Complete booking and persist to calendar/bill
  // ==========================================================================
  const completeBooking = useCallback(async (pin: ExplorePin, transport: TransportChoice) => {
    if (!onBookingCreated) {
      console.warn('[EXPLORE] No booking handler provided');
      return;
    }

    // Create experience booking for calendar
    const now = new Date();
    const visitTime = new Date(now);
    visitTime.setHours(14, 0, 0, 0); // Default to 2pm today

    // If it's already past 2pm, schedule for tomorrow
    if (now.getHours() >= 14) {
      visitTime.setDate(visitTime.getDate() + 1);
    }

    try {
      // Create the experience booking
      await onBookingCreated({
        serviceId: `explore_${pin.id}`,
        serviceName: pin.name,
        category: 'ACTIVITY',
        datetime: visitTime.toISOString(),
        durationMinutes: 120, // 2 hour default
        price: null,
        location: pin.subtitle,
      });

      // If taxi selected, add taxi charge to bill
      if (transport === 'taxi') {
        await onBookingCreated({
          serviceId: `taxi_${pin.id}`,
          serviceName: `Taxi to ${pin.name}`,
          category: 'TRANSPORT',
          datetime: visitTime.toISOString(),
          durationMinutes: 30,
          price: 15, // Fixed taxi fare
          location: 'Hotel Pickup',
        });
      }

      console.log('[EXPLORE] Booking completed:', pin.name, 'Transport:', transport);
      setBookingState('booked');
      setShowBookingConfirmation(true);

      // Speak confirmation
      const transportText = transport === 'taxi'
        ? "I've arranged a taxi for you."
        : transport === 'walk'
        ? "It's a lovely walk."
        : transport === 'public'
        ? "The tram is a scenic route."
        : "";

      zaxiaVoice.speak(`Perfect. Your visit to ${pin.name} is confirmed. ${transportText} It's been added to your calendar.`);

      // Return to map after 4 seconds
      setTimeout(() => {
        setShowBookingConfirmation(false);
        setShowPanel(false);
        setSelectedPin(null);
        setBookingState('idle');
        setTransportChoice(null);
      }, 4000);

    } catch (error) {
      console.error('[EXPLORE] Booking failed:', error);
      zaxiaVoice.speak("I'm sorry, there was a problem making that booking. Please try again.");
      setBookingState('idle');
    }
  }, [onBookingCreated]);

  // ==========================================================================
  // Phase D3.3: Voice command processor with booking flow
  // Phase D4.5.2: Uses ZaxiaVoiceEngine for command processing
  // ==========================================================================

  const handleVoiceAction = useCallback((action: VoiceAction, transcript: string) => {
    console.log('[EXPLORE] Handling voice action:', action.type, 'State:', bookingState, transcript);

    // ==========================================================================
    // Handle confirmation state
    // ==========================================================================
    if (bookingState === 'confirming' && selectedPin) {
      switch (action.type) {
        case 'CONFIRM':
        case 'YES':
        case 'EXPLORE_CONFIRM_BOOKING':
          setVoiceFeedback('Booking confirmed!');
          completeBooking(selectedPin, transportChoice);
          return;

        case 'CANCEL':
        case 'NO':
        case 'EXPLORE_CANCEL_BOOKING':
          setBookingState('idle');
          setTransportChoice(null);
          zaxiaVoice.speak("No problem. Let me know if you'd like to book something else.");
          setVoiceFeedback('Booking cancelled');
          return;

        case 'UNKNOWN':
          zaxiaVoice.speak("Just say 'yes' to confirm, or 'no' to cancel.");
          setVoiceFeedback("Say 'yes' or 'no'");
          return;

        default:
          // Continue to handle other actions
          break;
      }
    }

    // ==========================================================================
    // Handle transport selection state
    // ==========================================================================
    if (bookingState === 'askingTransport' && selectedPin) {
      switch (action.type) {
        case 'EXPLORE_SELECT_TRANSPORT':
          setTransportChoice(action.payload.transport);
          setBookingState('confirming');
          if (action.payload.transport === 'taxi') {
            zaxiaVoice.speak(`Great, I'll arrange a taxi. That's about €15. Shall I confirm your visit to ${selectedPin.name}?`);
            setVoiceFeedback('Taxi selected - confirm?');
          } else if (action.payload.transport === 'walk') {
            zaxiaVoice.speak(`Perfect, it's a pleasant walk. Shall I confirm your visit to ${selectedPin.name}?`);
            setVoiceFeedback('Walking - confirm?');
          } else {
            zaxiaVoice.speak(`Good choice, the tram is scenic. Shall I confirm your visit to ${selectedPin.name}?`);
            setVoiceFeedback('Public transport - confirm?');
          }
          return;

        case 'NO':
        case 'CANCEL':
          setTransportChoice(null);
          setBookingState('confirming');
          zaxiaVoice.speak(`No problem. Shall I confirm your visit to ${selectedPin.name}?`);
          setVoiceFeedback('No transport - confirm?');
          return;

        case 'BACK':
        case 'EXPLORE_BACK':
          setBookingState('idle');
          setTransportChoice(null);
          zaxiaVoice.speak("Alright, let me know if you'd like anything else.");
          setVoiceFeedback('Booking cancelled');
          return;

        case 'UNKNOWN':
          zaxiaVoice.speak("Would you like a taxi, walk, or take public transport?");
          setVoiceFeedback("Say 'taxi', 'walk', or 'public transport'");
          return;

        default:
          break;
      }
    }

    // ==========================================================================
    // Handle main actions
    // ==========================================================================
    switch (action.type) {
      // Initiate booking (user says "book this", "let's go", etc.)
      case 'EXPLORE_INITIATE_BOOKING':
        if (showPanel && selectedPin && bookingState === 'idle') {
          setBookingState('askingTransport');
          zaxiaVoice.speak(`Wonderful choice. Would you like me to arrange transport to ${selectedPin.name}? I can book a taxi, or you can walk or take public transport.`);
          setVoiceFeedback('Choose transport method');
        } else if (!showPanel) {
          zaxiaVoice.speak("Please select a location first. You can say 'museum', 'beach', or 'restaurant'.");
          setVoiceFeedback('Select a location first');
        }
        break;

      // Booking confirmation (in confirm step)
      case 'EXPLORE_CONFIRM_BOOKING':
        if (showPanel && selectedPin && bookingState === 'idle') {
          setBookingState('askingTransport');
          zaxiaVoice.speak(`Wonderful choice. Would you like me to arrange transport to ${selectedPin.name}? I can book a taxi, or you can walk or take public transport.`);
          setVoiceFeedback('Choose transport method');
        }
        break;

      // Ask about location
      case 'EXPLORE_ASK_ABOUT':
        if (showPanel && selectedPin) {
          const query = action.payload.query.toLowerCase();
          if (/more|describe|what\s*is/i.test(query)) {
            zaxiaVoice.speak(selectedPin.voiceResponses.tellMeMore);
            setVoiceFeedback('Telling you more...');
          } else if (/far|distance|long/i.test(query)) {
            zaxiaVoice.speak(selectedPin.voiceResponses.isFar);
            setVoiceFeedback('Distance info');
          } else if (/get\s*there|direction|way/i.test(query)) {
            zaxiaVoice.speak(selectedPin.voiceResponses.howToGetThere);
            setVoiceFeedback('Travel info');
          } else {
            zaxiaVoice.speak(selectedPin.voiceResponses.whatIsThis);
            setVoiceFeedback('About this place');
          }
        }
        break;

      // Select pin
      case 'EXPLORE_SELECT_PIN':
        selectPinByType(action.payload.pinType as ExplorePin['type']);
        setVoiceFeedback(`Showing ${action.payload.pinType}`);
        break;

      // Zoom out to show all pins
      case 'EXPLORE_ZOOM_OUT':
        setShowPanel(false);
        setSelectedPin(null);
        setBookingState('idle');
        setTransportChoice(null);
        setZoomOutTrigger(prev => prev + 1);
        zaxiaVoice.speak('Showing all locations.');
        setVoiceFeedback('Showing all locations');
        break;

      // Back/Close
      case 'CLOSE':
      case 'BACK':
      case 'EXPLORE_BACK':
        // If in booking flow, cancel it first
        if (bookingState !== 'idle') {
          setBookingState('idle');
          setTransportChoice(null);
          zaxiaVoice.speak('Booking cancelled. Back to exploring.');
          setVoiceFeedback('Booking cancelled');
          return;
        }

        if (showPanel) {
          setShowPanel(false);
          setSelectedPin(null);
          zaxiaVoice.speak('Back to the map.');
          setVoiceFeedback('Back to map');
        } else {
          zaxiaVoice.speak('Going back.');
          setVoiceFeedback('Going back...');
          setTimeout(() => onClose(), 500);
        }
        break;

      // Cancel booking
      case 'CANCEL':
      case 'EXPLORE_CANCEL_BOOKING':
        if (bookingState !== 'idle') {
          setBookingState('idle');
          setTransportChoice(null);
          zaxiaVoice.speak("No problem. Let me know if you'd like to book something else.");
          setVoiceFeedback('Booking cancelled');
        } else if (showPanel) {
          setShowPanel(false);
          setSelectedPin(null);
          zaxiaVoice.speak('Back to the map.');
          setVoiceFeedback('Back to map');
        }
        break;

      // Unknown - try to find pin type in transcript
      case 'UNKNOWN': {
        const lower = action.payload.transcript.toLowerCase();

        // Try to initiate booking if panel is open
        if (showPanel && selectedPin && bookingState === 'idle') {
          if (/\b(book\s*(this|it)?|i\s*want\s*to\s*go|reserve|reservation|make\s*a?\s*(booking|reservation)|let'?s\s*go|visit|i'?d\s*like\s*to\s*go)\b/i.test(lower)) {
            setBookingState('askingTransport');
            zaxiaVoice.speak(`Wonderful choice. Would you like me to arrange transport to ${selectedPin.name}? I can book a taxi, or you can walk or take public transport.`);
            setVoiceFeedback('Choose transport method');
            return;
          }

          // Try detail questions
          if (/\b(tell\s*me\s*more|more\s*(info|details|about)|what\s*is\s*this(\s*place)?|describe)\b/i.test(lower)) {
            zaxiaVoice.speak(selectedPin.voiceResponses.tellMeMore);
            setVoiceFeedback('Telling you more...');
            return;
          }

          if (/\b(is\s*it\s*far|how\s*far|far\s*away|distance|how\s*long\s*to\s*get)\b/i.test(lower)) {
            zaxiaVoice.speak(selectedPin.voiceResponses.isFar);
            setVoiceFeedback('Distance info');
            return;
          }

          if (/\b(how\s*(do\s*i|to|can\s*i)\s*get\s*there|directions?|way\s*there|getting\s*there)\b/i.test(lower)) {
            zaxiaVoice.speak(selectedPin.voiceResponses.howToGetThere);
            setVoiceFeedback('Travel info');
            return;
          }
        }

        // Try to find pin type using parseExploreVoice
        const pinType = parseExploreVoice(lower);
        if (pinType) {
          selectPinByType(pinType);
          setVoiceFeedback(`Showing ${pinType}`);
          return;
        }

        // Check for zoom out commands
        if (/\b(zoom\s*out|show\s*(all|the)?\s*(pins?|locations?|map)|full\s*map|see\s*(all|everything)|overview)\b/i.test(lower)) {
          setShowPanel(false);
          setSelectedPin(null);
          setBookingState('idle');
          setTransportChoice(null);
          setZoomOutTrigger(prev => prev + 1);
          zaxiaVoice.speak('Showing all locations.');
          setVoiceFeedback('Showing all locations');
          return;
        }

        // Fallback - couldn't understand
        if (showPanel && bookingState === 'idle') {
          zaxiaVoice.speak("I didn't catch that. You can say 'Book this', 'Tell me more', or 'Go back'.");
          setVoiceFeedback("Try 'Book this' or 'Tell me more'");
        } else if (!showPanel) {
          zaxiaVoice.speak("I didn't catch that. Try saying 'show the museum', 'beach', or 'restaurant'.");
          setVoiceFeedback("Try 'museum', 'beach', or 'restaurant'");
        }
        break;
      }

      default:
        console.log('[EXPLORE] Unhandled action type:', action.type);
        break;
    }
  }, [showPanel, selectedPin, onClose, selectPinByType, bookingState, transportChoice, completeBooking, setZoomOutTrigger]);

  const processVoiceCommand = useCallback((transcript: string) => {
    console.log('[EXPLORE] Processing voice command:', transcript, 'State:', bookingState);

    // Determine booking state for context
    let exploreBookingState: 'idle' | 'askingTransport' | 'confirming' = 'idle';
    if (bookingState === 'askingTransport') exploreBookingState = 'askingTransport';
    else if (bookingState === 'confirming') exploreBookingState = 'confirming';

    // Use the central voice engine to parse the command
    const voiceContext = createVoiceContext('explore', {
      subContext: {
        exploreBookingState,
        exploreHasSelectedPin: selectedPin !== null,
      },
    });
    const action = processVoiceInput(transcript, voiceContext);

    handleVoiceAction(action, transcript);
  }, [bookingState, selectedPin, handleVoiceAction]);

  const startVoiceListening = useCallback(() => {
    if (!isSpeechRecognitionAvailable()) {
      setVoiceFeedback('Voice not supported');
      return;
    }

    // Cancel any ongoing speech
    zaxiaVoice.cancel();

    // Cancel any existing recognition
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }

    const recognition = createSpeechRecognition();
    if (!recognition) return;

    recognitionRef.current = recognition;
    setVoiceState('listening');
    setVoiceTranscript('');
    transcriptRef.current = '';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result?.[0]) {
          if (result.isFinal) {
            finalTranscript += result[0].transcript;
          } else {
            interimTranscript += result[0].transcript;
          }
        }
      }

      const currentTranscript = finalTranscript || interimTranscript;
      setVoiceTranscript(currentTranscript);
      transcriptRef.current = currentTranscript;
    };

    recognition.onerror = () => {
      setVoiceState('idle');
      setVoiceFeedback(null);
    };

    recognition.onend = () => {
      // Recognition ended naturally
    };

    recognition.start();
  }, []);

  const stopVoiceAndProcess = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // Ignore errors when stopping
      }
      recognitionRef.current = null;
    }

    const finalTranscript = transcriptRef.current.trim();

    if (!finalTranscript) {
      setVoiceState('idle');
      setVoiceFeedback(null);
      return;
    }

    setVoiceState('processing');
    processVoiceCommand(finalTranscript);

    // Clear feedback after a delay
    setTimeout(() => {
      setVoiceState('idle');
      setVoiceFeedback(null);
    }, 2000);
  }, [processVoiceCommand]);

  // Clear voice feedback after timeout
  useEffect(() => {
    if (voiceFeedback) {
      const timer = setTimeout(() => setVoiceFeedback(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [voiceFeedback]);

  // Panel action callbacks — declared before the keyboard useEffect to avoid TDZ
  const handlePinClick = useCallback((pin: ExplorePin, index: number) => {
    setFocusedPinIndex(index);
    setSelectedPin(pin);
    setShowPanel(true);
    // Use concierge-style introduction
    zaxiaVoice.speak(pin.voiceResponses.whatIsThis);
  }, []);

  const handleClosePanel = useCallback(() => {
    setShowPanel(false);
    setSelectedPin(null);
    setBookingState('idle');
    setPanelFocusIndex(0);
    setZoomOutTrigger(prev => prev + 1);
  }, []);

  const handleBookThis = useCallback(() => {
    if (!selectedPin) return;
    setPanelFocusIndex(0);
    setBookingState('askingTransport');
    zaxiaVoice.speak(`Wonderful choice. Would you like me to arrange transport to ${selectedPin.name}? Choose below or say taxi, walk, or public transport.`);
  }, [selectedPin]);

  const handleSelectTransport = useCallback((t: 'taxi' | 'walk' | 'public') => {
    if (!selectedPin) return;
    setTransportChoice(t);
    setPanelFocusIndex(0);
    setBookingState('confirming');
    if (t === 'taxi') {
      zaxiaVoice.speak(`Great, I'll arrange a taxi. That's about €15. Confirm your visit to ${selectedPin.name}?`);
    } else if (t === 'walk') {
      zaxiaVoice.speak(`Perfect. Confirm your visit to ${selectedPin.name}?`);
    } else {
      zaxiaVoice.speak(`Good choice. Confirm your visit to ${selectedPin.name}?`);
    }
  }, [selectedPin]);

  const handleConfirmBooking = useCallback(() => {
    if (!selectedPin) return;
    completeBooking(selectedPin, transportChoice);
  }, [selectedPin, transportChoice, completeBooking]);

  const handleCancelBooking = useCallback(() => {
    setPanelFocusIndex(0);
    setBookingState('idle');
    setTransportChoice(null);
    zaxiaVoice.speak("No problem. Let me know if you'd like to book something else.");
  }, []);

  // Keyboard navigation (with local voice input on Space)
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // --- Panel open: D-Pad navigates action buttons, not the map ---
      if (showPanel) {
        switch (e.key) {
          case 'ArrowLeft': {
            e.preventDefault();
            setPanelFocusIndex(prev => Math.max(0, prev - 1));
            return;
          }
          case 'ArrowRight': {
            e.preventDefault();
            const maxIdx = bookingState === 'idle' ? 1
              : bookingState === 'askingTransport' ? 3 : 1;
            setPanelFocusIndex(prev => Math.min(maxIdx, prev + 1));
            return;
          }
          case 'Enter': {
            e.preventDefault();
            if (bookingState === 'idle') {
              if (panelFocusIndex === 0) handleBookThis();
              else handleClosePanel();
            } else if (bookingState === 'askingTransport') {
              const transportKeys = ['taxi', 'walk', 'public'] as const;
              if (panelFocusIndex < 3) handleSelectTransport(transportKeys[panelFocusIndex]!);
              else handleCancelBooking();
            } else if (bookingState === 'confirming') {
              if (panelFocusIndex === 0) handleConfirmBooking();
              else handleCancelBooking();
            }
            return;
          }
          case 'Escape':
          case 'Backspace': {
            e.preventDefault();
            if (bookingState !== 'idle') {
              handleCancelBooking();
            } else {
              handleClosePanel();
            }
            return;
          }
          case ' ': {
            // Keep voice available even when panel is open
            e.preventDefault();
            e.stopPropagation();
            if (voiceState === 'idle') {
              startVoiceListening();
            }
            return;
          }
        }
        // Any other key while panel open — don't fall through to map nav
        return;
      }

      // --- No panel: map / pin cycling ---
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          setFocusedPinIndex(prev => (prev - 1 + EXPLORE_PINS.length) % EXPLORE_PINS.length);
          setShowPanel(false);
          setSelectedPin(null);
          break;
        case 'ArrowRight':
          e.preventDefault();
          setFocusedPinIndex(prev => (prev + 1) % EXPLORE_PINS.length);
          setShowPanel(false);
          setSelectedPin(null);
          break;
        case 'Enter':
          e.preventDefault();
          const pin = EXPLORE_PINS[focusedPinIndex];
          if (pin) {
            setSelectedPin(pin);
            setShowPanel(true);
            // Use concierge-style introduction
            zaxiaVoice.speak(pin.voiceResponses.whatIsThis);
          }
          break;
        case ' ':
          // Space starts voice input (prevents Ask Zaxia from opening)
          e.preventDefault();
          e.stopPropagation();
          if (voiceState === 'idle') {
            startVoiceListening();
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        case 'Backspace':
          e.preventDefault();
          onClose();
          break;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ' && voiceState === 'listening') {
        e.preventDefault();
        e.stopPropagation();
        stopVoiceAndProcess();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true); // Use capture phase
    window.addEventListener('keyup', handleKeyUp, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [isActive, focusedPinIndex, showPanel, bookingState, panelFocusIndex, voiceState, onClose, startVoiceListening, stopVoiceAndProcess, handleBookThis, handleClosePanel, handleSelectTransport, handleCancelBooking, handleConfirmBooking]);

  const getMarkerColor = (type: ExplorePin['type']) => {
    switch (type) {
      case 'museum': return '#8b5cf6';
      case 'beach': return '#06b6d4';
      case 'restaurant': return '#f97316';
      default: return '#6b7280';
    }
  };

  return (
    <motion.div
      className="explore-view h-full w-full flex flex-col bg-zinc-950 rounded-xl overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div className="explore-header flex items-center px-6 py-4 bg-zinc-900/50">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🗺️</span>
          <h2 className="text-xl font-light text-white/90 tracking-wide">
            Explore Nearby
          </h2>
        </div>
      </div>

      {/* Map + Panel Container */}
      <div className="flex-1 relative overflow-hidden">
        {/* Map section - adjusts width based on panel */}
        <motion.div
          className="absolute top-0 left-0 h-full"
          initial={{ width: '100%' }}
          animate={{ width: showPanel ? '35%' : '100%' }}
          transition={{ type: 'tween', duration: 0.22, ease: 'easeOut' }}
        >
          <MapContainer
            center={[LISBON_MAP.coordinates.lat, LISBON_MAP.coordinates.lng]}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
            attributionControl={false}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <MapController
              selectedPin={selectedPin}
              shouldFitBounds={isActive}
              isPanelOpen={showPanel}
              triggerZoomOut={zoomOutTrigger}
            />

            {/* Hotel Marker */}
            <Marker
              position={[LISBON_MAP.coordinates.lat, LISBON_MAP.coordinates.lng]}
              icon={hotelIcon}
            >
              <Popup>
                <div className="text-center p-1">
                  <strong>{LISBON_MAP.hotelName}</strong>
                  <br />
                  <span className="text-gray-500 text-sm">Your Hotel</span>
                </div>
              </Popup>
            </Marker>

            {/* Experience Pins */}
            {EXPLORE_PINS.map((pin: ExplorePin, index: number) => {
              const isFocused = focusedPinIndex === index;
              const isSelected = selectedPin?.id === pin.id;

              return (
                <Marker
                  key={pin.id}
                  position={[pin.coordinates.lat, pin.coordinates.lng]}
                  icon={createIcon(pin.icon, getMarkerColor(pin.type), isFocused || isSelected)}
                  eventHandlers={{
                    click: () => handlePinClick(pin, index),
                  }}
                >
                  <Popup>
                    <div className="text-center p-1">
                      <strong>{pin.name}</strong>
                      <br />
                      <span className="text-gray-500 text-sm">{pin.distance}</span>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>

          {/* Pin indicator */}
          <div className="absolute bottom-6 right-6 z-[1000]">
            <div className="flex items-center gap-2 bg-white/95 backdrop-blur-sm px-3 py-2 rounded-full shadow-lg">
              {EXPLORE_PINS.map((pin: ExplorePin, index: number) => (
                <motion.div
                  key={pin.id}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                    focusedPinIndex === index
                      ? 'bg-blue-500 scale-125'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </motion.div>

        {/* Experience Detail Panel - full height overlay */}
        <ExperienceDetailPanel
          pin={selectedPin}
          isOpen={showPanel}
          onClose={handleClosePanel}
          bookingState={bookingState}
          transportChoice={transportChoice}
          showConfirmation={showBookingConfirmation}
          onBookThis={handleBookThis}
          onSelectTransport={handleSelectTransport}
          onConfirmBooking={handleConfirmBooking}
          onCancelBooking={handleCancelBooking}
          panelFocusIndex={panelFocusIndex}
        />
      </div>

      {/* Footer with voice indicator */}
      <div className="px-6 py-3 border-t border-white/[0.04]">
        {/* Voice indicator - shows when listening or has feedback */}
        <AnimatePresence>
          {(voiceState !== 'idle' || voiceFeedback) && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="flex items-center justify-center gap-3 mb-3"
            >
              {voiceState === 'listening' && (
                <>
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 1.2, ease: 'easeInOut' }}
                    className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center"
                  >
                    <Mic className="w-4 h-4 text-amber-400" />
                  </motion.div>
                  <span className="text-sm text-white/60">
                    {voiceTranscript || 'Listening...'}
                  </span>
                </>
              )}
              {voiceState === 'processing' && (
                <span className="text-sm text-white/60">Processing...</span>
              )}
              {voiceState === 'idle' && voiceFeedback && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-sm text-amber-400"
                >
                  {voiceFeedback}
                </motion.span>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
